import { proxy } from 'valtio';

import { internalSites } from '@canvas/constants';
import { Deferrable } from '@canvas/deferrable';

import { type FeatureFlagOverrides } from './get-feature-flags-from-url';

export enum FeatureFlagSource {
  /** Flags used by our code but not managed by LD or Statsig */
  Local = 'local',
  /** Flags managed by LD. No new feature flags should be created using launchdarkly. All new
   *  flags must use Statsig moving forward.
   *  @see runbook: docs/guides/how-to-create-a-feature-flag-statsig.md
   */
  DEPRECATED_LaunchDarkly = 'launchdarkly',
  /** Flags managed by Statsig */
  Statsig = 'statsig',
}

export type ClientFeatureFlagName = keyof typeof defaultFeatureFlagState;
export type ClientFeatureFlag =
  | {
      enabled: boolean;
      source: FeatureFlagSource.Local;
    }
  | {
      enabled: boolean;
      flag: string;
      source: FeatureFlagSource.DEPRECATED_LaunchDarkly | FeatureFlagSource.Statsig;
    };
export type ClientFeatureFlags = Record<ClientFeatureFlagName, ClientFeatureFlag>;
/**
 * All feature flags must be disabled by default.
 * @description Default feature flag values cannot be mutated.
 *
 * @remark: You can locally override these values in your development environment by:
 * - adding them to .ff-localdev, which will persist across app restarts
 * - adding the query param ?df=<flagName> to the URL, which works in dev and staging
 * - using the dev-tools panel in local dev
 */
export const defaultFeatureFlagState = {
  /**
   * This flag enables/disables the experimental feature of improving shapeable path
   * resizing for multi-select resizing.
   */
  shapeablePathResizing: {
    enabled: false,
    source: FeatureFlagSource.Local,
  },
  /**
   * This flag enables/disables the ability to resize Smart Links. Flag lives at
   * https://switcheroo.atlassian.com/ui/gates/33775dc9-8f31-47f9-b727-a726fbc2ff69/key/cc-whiteboards-smart-links-resizing
   */
  smartLinksResizing: {
    enabled: false,
    flag: 'cc-whiteboards-smart-links-resizing',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * This flag enables or disables the ability for the user to resize elements
   * on the page using the new split DOM handles. This is particularly notable
   * when resizing aspect-ratio locked or font sizing elements.
   */
  splitDomHandlesForResize: {
    enabled: true,
    source: FeatureFlagSource.Local,
  },
  /**
   * This flag enables/disables the ability to resize multiple entities at the same time.
   * Note that this will be a long standing hidden feature: Acceptance criteria
   * is that all elements must be resizable with multi select before this can be enabled.
   */
  multiSelectResize: {
    enabled: false,
    source: FeatureFlagSource.Statsig,
    flag: 'whiteboards_-_multi_select_resize_support',
  },
  /**
   * Used for testing purposes. Do not use otherwise.
   * @deprecated
   */
  test: { enabled: false, source: FeatureFlagSource.Local },
  /**
   * Holding shift while dragging will use snap to grid instead of the weird
   * snapping logic we currently have which locks the X and Y on drag
   * https://switcheroo.atlassian.com/ui/gates/847b94df-c70b-429d-bc65-0e4db3572485
   */
  snapToGrid: {
    enabled: false,
    source: FeatureFlagSource.Statsig,
    flag: 'cc-whiteboards-enable-snap-to-grid-using-shift',
  },
  /**
   * Enables updates via http when websocket has had multiple failed connection attempts
   * https://app.launchdarkly.com/canvas/staging/features/should-update-via-http-when-websocket-wont-connect/targeting
   */
  shouldUpdateViaHttpWhenWebsocketWontConnect: {
    enabled: false,
    flag: 'should-update-via-http-when-websocket-wont-connect',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables adding debug report to sentry errors
   */
  shouldAddDebugReportToSentryErrors: {
    enabled: false,
    source: FeatureFlagSource.Local,
  },
  /**
   * Enables using graphQL over REST for the session API (eg to get access and media tokens,
   * expiration, feature entitlements and the edition).
   * Note: is a local-only flag and SHOULD NOT have a DEPRECATED_LaunchDarkly flag as we don't want to block
   * app startup on the FF client
   */
  enableGraphqlSessionApi: {
    enabled: false,
    source: FeatureFlagSource.Local,
  },
  /**
   * Enables the loom integration with whiteboards.
   * Note, the actual usage is also dependent on the confluence gate as well
   */
  whiteboardsLoomIntegration: {
    enabled: false,
    flag: 'whiteboardsloomintegration',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Change the url we call from the client to stargate to make use of the new
   * archetype stargate route
   * https://switcheroo.atlassian.com/gates/88ccf24a-d511-4a42-b627-9e77befa9d59
   */
  stargateRoutingUseArchetype: {
    enabled: false,
    flag: 'stargate_routing_use_archetype',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the voting feature for whiteboards
   * https://switcheroo.atlassian.com/gates/e796b565-25bb-46f6-a445-fce4cf1cfb33
   */
  voting: {
    enabled: false,
    flag: 'should_enable_voting',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the sidebar panel for the voting feature, as part of the M3 release.
   * https://switcheroo.atlassian.com/ui/gates/da096917-cad2-44d4-96ae-7ef70d2c26d7
   */
  votingM3: {
    enabled: false,
    flag: 'should_enable_voting_m3',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Align elements flag to enable aligning elements as a feature
   */
  alignElements: {
    enabled: false,
    flag: 'cc-whiteboards-align-elements',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * A Feature Flag for Miro Importer
   * https://switcheroo.atlassian.com/ui/gates/fcd8320b-7b87-4ea3-9b79-9226c811ed3d
   */
  miroImporter: {
    enabled: false,
    flag: 'cc-whiteboards-miro-import',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * A Feature Flag for uplifting our Jira integrations
   * https://switcheroo.atlassian.com/ui/gates/3ff6e04b-fcca-46ba-9115-58a077c294f8
   */
  whiteboardsJiraV2: {
    enabled: false,
    flag: 'whiteboards_jira_v2',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * A Feature Flag for exposing hyperlinks
   * https://switcheroo.atlassian.com/ui/gates/fb48f68f-c5b5-4895-8601-0961d14d8076
   */
  hyperlinks: {
    enabled: false,
    flag: 'should-enable-hyperlinks',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables version history for whiteboards
   * https://switcheroo.atlassian.com/ui/gates/388c76d5-004a-48f6-b441-caa976a95214
   */
  versionHistory: {
    enabled: false,
    flag: 'whiteboards-version-history',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Uses explicit SPA navigation refactor instead of relying on token change.
   *
   */
  explicitSPANavigation: {
    enabled: false,
    source: FeatureFlagSource.Local,
  },

  /**
   * Enables the hiding of the beta lozenge in AI generate stickies.
   * https://switcheroo.atlassian.com/ui/gates/c9678b5b-6fab-4495-8893-75c079f864b6/key/cc-whiteboards-ai-generate-hide-beta-lozenge
   */
  aiGenerateHideBetaLozenge: {
    enabled: false,
    flag: 'cc-whiteboards-ai-generate-hide-beta-lozenge',
    source: FeatureFlagSource.Statsig,
  },

  /**
   * Enables the display of sources in AI generate stickies.
   * https://switcheroo.atlassian.com/ui/gates/23f4e95c-95e8-4411-a74b-de0bf463017d/key/cc-whiteboards-ai-generate-sources
   */
  aiGenerateSources: {
    enabled: false,
    flag: 'cc-whiteboards-ai-generate-sources',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the AI Smart Button and connection to the Confluence right panel rendering agent capabilities
   * https://switcheroo.atlassian.com/ui/gates/fa7497cc-b87f-483b-b6ea-e1457671c9f0/key/should-enable-whiteboard-ai-smart-button
   */
  aiSmartButton: {
    enabled: false,
    flag: 'should-enable-whiteboard-ai-smart-button',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables uploading and downloading of videos.
   * https://switcheroo.atlassian.com/ui/gates/f2e7df4d-c463-4ee4-9060-f7acf0eb2eb7
   */
  video: {
    enabled: false,
    flag: 'canvas-should-enable-videos',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables v2 permissions infrastructure using policies.
   * https://switcheroo.atlassian.com/ui/gates/608631c1-d60f-4030-bba9-2c40a115b6d4
   */
  v2Permissions: {
    enabled: false,
    flag: 'should-use-v2-permissions',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables v2 permissions infrastructure using policies for reads.
   * https://switcheroo.atlassian.com/ui/gates/1ee0ee5f-7f0f-4878-a120-6ad283799cd9/key/should-use-v2-permissions-on-reads-from-policies
   */
  v2PermissionsReadsFromPolicies: {
    enabled: false,
    flag: 'should-use-v2-permissions-on-reads-from-policies',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enabled a low quality mode if only software rendering is available
   * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/whiteboard-should-enable-low-quality-mode
   */
  lowQualityMode: {
    enabled: false,
    flag: 'whiteboard-should-enable-low-quality-mode',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the "retry" button for AI clustering/smart grouping feature.
   *
   * Should only be enabled once AI clustering supports responding with a
   * different cluster on each request
   */
  aiClusteringRetry: {
    enabled: false,
    flag: 'should_enable_ai_clustering_retry_button',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the client fetching templates via the CDN
   * https://switcheroo.atlassian.com/ui/gates/c84621b5-b469-4fa7-8919-e936c555d81d/key/whiteboards-cdn-templates
   */
  cdnTemplates: {
    enabled: false,
    flag: 'whiteboards-cdn-templates',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the client fetching templates via the CDN
   * https://switcheroo.atlassian.com/ui/gates/d5b79dd4-71fa-44de-8856-c84e245647ca/key/whiteboards-private-mode
   */
  privateMode: {
    enabled: false,
    flag: 'whiteboards-private-mode',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the features implemented during our Nov 2024 ITG UX Health Week
   * https://switcheroo.atlassian.com/ui/gates/3db8da95-85ee-4e07-8211-e773188b4896/key/confluence_whiteboards_ux_health_week_nov_2024
   */
  uxHealthWeekNov2024: {
    enabled: false,
    flag: 'confluence_whiteboards_ux_health_week_nov_2024',
    source: FeatureFlagSource.Statsig,
  },

  /**
   * Enable animations on element toolbar and on the secondary items on the main toolbar
   * https://switcheroo.atlassian.com/ui/gates/8ac87315-a63b-48a5-8075-3de80116fa31/key/cc-whiteboards-uxhealthweekpt2
   */
  uxHealthWeekAnimations: {
    enabled: false,
    flag: 'cc-whiteboards-uxhealthweekpt2',
    source: FeatureFlagSource.Statsig,
  },

  /**
   * Enable animations on element toolbar and on the secondary items on the main toolbar
   * https://switcheroo.atlassian.com/ui/gates/0e548680-88d7-4284-ab4b-d02ba43e25b5/key/cc-whiteboards-main-toolbar-animations
   */
  allowMainToolbarAnimations: {
    enabled: false,
    flag: 'cc-whiteboards-main-toolbar-animations',
    source: FeatureFlagSource.Statsig,
  },

  /**
   * Enables the follow cursor changes implemented during our Nov 2024 ITG UX Health Week
   * https://switcheroo.atlassian.com/ui/gates/33685027-5d45-4173-bad3-3005d245b1ef/key/whiteboards_ux_health_week_follow_cursor
   */
  uxHealthWeekNov2024FollowCursor: {
    enabled: false,
    flag: 'whiteboards_ux_health_week_follow_cursor',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the whiteboards card element
   * https://switcheroo.atlassian.com/ui/gates/f2c97645-5a28-422d-83aa-fc7c59c45e71/key/should-enable-whiteboard-card
   */
  cardElement: {
    enabled: false,
    flag: 'should-enable-whiteboard-card',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the whiteboards draggable line labels
   * https://switcheroo.atlassian.com/ui/gates/b1fa8851-42b0-41f5-8ec7-a13a261c78a3/key/whiteboards-enable-draggable-line-labels
   */
  draggableLineLabels: {
    enabled: false,
    flag: 'whiteboards-enable-draggable-line-labels',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the distribute spacing and vertical text UX health features.
   * https://switcheroo.atlassian.com/ui/gates/6d81e5f0-09dd-4775-b159-3bb1ecf91689/key/whiteboards-ux-health-distribute-and-text
   */
  distributeSpacingAndVerticalText: {
    enabled: false,
    flag: 'whiteboards-ux-health-distribute-and-text',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the vertical text alignment UX Health feature.
   * https://switcheroo.atlassian.com/ui/gates/dfd6e58f-85f9-4494-affe-4495fd552055/key/cc-whiteboards-vertical-alignment
   */
  verticalTextAlignment: {
    enabled: false,
    flag: 'cc-whiteboards-vertical-alignment',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the mural whiteboard importer feature.
   * https://switcheroo.atlassian.com/ui/gates/1ac5c7ad-f5f8-4a9d-a7b3-361730c7b70c/key/cc-whiteboards-mural-importer
   */
  whiteboardMuralImporter: {
    enabled: false,
    flag: 'cc-whiteboards-mural-importer',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the accessibility module containing canvas as DOM, and other a11y features.
   * https://switcheroo.atlassian.com/ui/gates/aa2aba2d-0060-4b20-86e9-d3d44184369d/key/a11y-module
   */
  a11yModule: {
    enabled: false,
    flag: 'a11y-module',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables memory usage emitting
   * https://switcheroo.atlassian.com/ui/gates/ce4ab93c-604d-43ee-a363-20e181bedfed/key/whiteboards_should_emit_frontend_memory_metrics
   */
  shouldEmitFrontendMemoryMetrics: {
    enabled: false,
    flag: 'whiteboards_should_emit_frontend_memory_metrics',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the locale switcher, which is ONLY for whiteboards developers.
   * https://switcheroo.atlassian.com/ui/gates/ba151294-55cc-49c9-8b2a-af2f65e9a42e/key/enable_whiteboards_locale_switcher
   */
  enableWhiteboardsLocaleSwitcher: {
    enabled: false,
    flag: 'enable_whiteboards_locale_switcher',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the 'Find and Replace' feature in whiteboards.
   * https://switcheroo.atlassian.com/ui/gates/cc29b2d5-734c-43d3-af90-c47d3468780b/key/whiteboards_find_and_replace
   */
  whiteboardsFindAndReplace: {
    enabled: false,
    flag: 'whiteboards_find_and_replace',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the 'Mentions' feature in whiteboards.
   * https://switcheroo.atlassian.com/ui/gates/369886e5-11ef-4d83-ada3-7a630146303c/key/whiteboards_mentions
   */
  whiteboardsMentions: {
    enabled: false,
    flag: 'whiteboards_mentions',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the visual refresh for atlaskit components and colors
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/05b28d4c-67f1-45db-a0c6-4c14ec40185c/key/platform-component-visual-refresh
   */
  visualRefreshComponentsAndColors: {
    enabled: false,
    flag: 'platform-component-visual-refresh',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the visual refresh for platform icons.
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/e36c4a44-2846-4b76-b262-40e1d1e3932e/key/platform-visual-refresh-icons
   */
  visualRefreshIcons: {
    enabled: false,
    flag: 'platform-visual-refresh-icons',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Whether or not to proceed with using the dirty status of the whiteboard, and whether or not to proceed with attempting to properly clean up the dirty board
   * https://switcheroo.atlassian.com/ui/gates/aa6287b7-6739-445d-b312-06ae6446f3e2/key/clean_dirty_whiteboard
   */
  cleanDirtyWhiteboard: {
    enabled: false,
    flag: 'clean_dirty_whiteboard',
    source: FeatureFlagSource.Statsig,
  },
  /*
   * Enables content wrapper - header in confluence
   * https://switcheroo.atlassian.com/ui/gates/86ef3e79-79a9-4799-b061-9e7f0cf3f36b/key/confluence_frontend_object_header
   */
  confluenceContentWrapperHeader: {
    enabled: false,
    flag: 'confluence_frontend_object_header',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Telepointers refresh in whiteboards
   * https://switcheroo.atlassian.com/ui/gates/f7e07055-3849-43ab-9f15-7b0a3c8736ce/key/cc-whiteboards-telepointer-refresh
   */
  telepointersRefresh: {
    enabled: false,
    flag: 'cc-whiteboards-telepointer-refresh',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Exposes mindmaps as a feature
   * https://switcheroo.atlassian.com/ui/gates/659f56de-ea80-482e-a9f2-ae96b90e0fea/key/should_enable_mindmaps
   */
  mindmaps: {
    enabled: false,
    flag: 'should_enable_mindmaps',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Should it be possible to stack sections within sections - this modifies sections to follow
   * the same logic as other element types.
   */
  stackableSections: {
    enabled: false,
    flag: 'confluence-whiteboards-stackable-sections',
    source: FeatureFlagSource.Statsig,
  },
  devSocket: {
    enabled: false,
    flag: 'dev_socket_disconnect',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Sets element limit to 10k
   *
   * https://switcheroo.atlassian.com/ui/gates/f5530fa7-5083-4418-be45-208843122cb9/key/higher_element_board_limits
   */
  higherElementBoardLimits: {
    enabled: false,
    flag: 'higher_element_board_limits',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Updates media field settings for Field Settings 2.0 project
   *
   * https://switcheroo.atlassian.com/ui/gates/f5ff837f-5fd3-4049-9775-7af3c276b4fd/key/database-field-setting-2-media
   */
  mediaFieldSettings: {
    enabled: false,
    flag: 'database-field-setting-2-media',
    source: FeatureFlagSource.Statsig,
  },
  useOCRFallbackInTextParsing: {
    enabled: false,
    flag: 'use_ocr_fallback_in_text_parsing',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables throttling of awareness updates for selection and transient transaction channels
   * based on the number of collaborators
   *
   * https://switcheroo.atlassian.com/ui/gates/1dd2d683-40b7-4d82-8bcf-26e5e9aeb20a/key/canvas_throttle_awareness_updates
   */
  throttleAwarenessUpdates: {
    enabled: false,
    flag: 'canvas_throttle_awareness_updates',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Allows users to switch to an inline view for smart links
   */
  inlineViewForSmartLinks: {
    enabled: false,
    source: FeatureFlagSource.Local,
  },
  /**
   * Enables the use of content wrapper refresh in confluence
   * See: https://hello.atlassian.net/wiki/spaces/CAN/pages/5048306970/Feature+Content+wrapper+for+whiteboards#%F0%9F%96%BC%EF%B8%8F-The-feature
   *
   * https://switcheroo.atlassian.com/ui/gates/d4632be3-73c3-4ab2-babb-0ae7d7d07f19/key/should_use_content_wrapper_refresh
   */
  shouldUseContentWrapperRefresh: {
    enabled: false,
    flag: 'should_use_content_wrapper_refresh',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the pen tool
   * https://switcheroo.atlassian.com/ui/gates/b8481e8a-5982-49ae-a78a-efae264c54f5/key/should_enable_pen
   */
  pen: {
    enabled: false,
    flag: 'should_enable_pen',
    source: FeatureFlagSource.Statsig,
  },
} as const satisfies Record<string, ClientFeatureFlag>;

type Mutable<T> = { -readonly [P in keyof T]: Mutable<T[P]> };

/**
 * Initial state is set using the `useFeatureFlags` hook in `@canvas/app`.
 */
export const currentFeatureFlagState: Mutable<ClientFeatureFlags> = proxy(
  defaultFeatureFlagState
);

/**
 * If true, supplied feature flag is enabled.
 * @description Ensure you check this value after app boot.
 * @param flag
 * @returns
 */
export const isFeatureFlagEnabled = (flag: ClientFeatureFlagName) => {
  /**
   * The session init handlers are called before we get FF values from statsig,
   * hence using environments to control the behaviour.
   */
  if (flag === 'explicitSPANavigation') {
    return true;
  }

  return Boolean(currentFeatureFlagState[flag].enabled);
};

export const resetFeatureFlagState = () => {
  for (const featureFlag of Object.keys(
    defaultFeatureFlagState
  ) as (keyof typeof defaultFeatureFlagState)[]) {
    currentFeatureFlagState[featureFlag] = defaultFeatureFlagState[featureFlag];
  }
};

/**
 * Use this sparingly. Only to be used in cases where you truly, permanently want to allow a feature to be
 * used by the internal Atlassians (E.g. Showing banners, etc)
 */
export const isInternalSite = () =>
  internalSites
    .map((site) => site.hostname.toLowerCase())
    .concat(['canvas.localhost'])
    .includes(new URL(globalThis.location.href).hostname.toLowerCase());

export const launchDarklyClientInitialized = new Deferrable();
export const statsigClientInitalized = new Deferrable();

/**
 * A promise which resolves when the feature flag state has been initialized.
 * This will be resolved when both feature flag clients are ready.
 */
export const featureFlagsInitialized =
  // We can't wait for launch darkly/statsig in offline mode since we have no
  // network mocks for these features (would require reverse engineering their API)
  process.env.MODE === 'offline'
    ? Promise.resolve()
    : Promise.all([launchDarklyClientInitialized.get(), statsigClientInitalized.get()]);

export const initialiseLocalFeatureFlags = (
  featureFlagOverrides: FeatureFlagOverrides
) => {
  for (const flag of Object.keys(defaultFeatureFlagState) as ClientFeatureFlagName[]) {
    if (defaultFeatureFlagState[flag].source === FeatureFlagSource.Local) {
      currentFeatureFlagState[flag].enabled =
        featureFlagOverrides[flag] ?? defaultFeatureFlagState[flag].enabled;
    }
  }
};
